import BaseScene from "../../scenes/BaseScene";

/**
 * place holder shape types
 *
 * @export
 * @enum {number}
 */
export enum PHShape {
    RECT = 0,
    CIRCLE,
    ROUND_RECT,
}

export type PHDimensions = {
    width: number;
    height: number;
    radius: number;
};

/**
 * Placeholder plugin is used for creating placeholder images / sprites and physics sprites
 * I can draw rectangles, rounded rectangles and circles, of varying sizes and coulors
 * Caching of simular shapes is build in and everything is genreates white and tinted.
 *
 * @export
 * @class PlaceholderPlugin
 * @extends {Phaser.Plugins.BasePlugin}
 */
export default class PlaceholderPlugin extends Phaser.Plugins.BasePlugin {
    /**
     * @constructor Creates an instance of Placeholder plugin (that handles creation of placeholder images / sprites).
     * @param {Phaser.Plugins.PluginManager} pluginManager
     * @memberof GameData
     */
    constructor(pluginManager: Phaser.Plugins.PluginManager) {

        super(pluginManager);
        console.log("PlaceholderPlugin::constructor");

    }


    /**
     * new placeholder sprite
     *
     * @param {BaseScene} scene
     * @param {number} x
     * @param {number} y
     * @param {PHShape} shape
     * @param {PHDimensions} dimensions
     * @param {number} color
     * @returns {Phaser.GameObjects.Sprite}
     * @memberof PlaceholderPlugin
     */
    public sprite(scene: BaseScene, x: number,y: number,shape: PHShape, dimensions: PHDimensions, color: number): Phaser.GameObjects.Sprite {
        const key = this.generateTexture(scene,shape, dimensions);
        const sprite = scene.add.sprite(x,y,key);
        sprite.tint = color;
        return sprite;
    }

    /**
     * new placeholder physics sprite
     *
     * @param {BaseScene} scene
     * @param {number} x
     * @param {number} y
     * @param {PHShape} shape
     * @param {PHDimensions} dimensions
     * @param {number} color
     * @returns {Phaser.Physics.Arcade.Sprite}
     * @memberof PlaceholderPlugin
     */
    public physics(scene: BaseScene, x: number,y: number,shape: PHShape, dimensions: PHDimensions, color: number): Phaser.Physics.Arcade.Sprite {
        const key = this.generateTexture(scene,shape, dimensions);
        const sprite = scene.physics.add.sprite(x,y,key);
        sprite.tint = color;
        return sprite;
    }

    /**
     * new placeholder image
     *
     * @param {BaseScene} scene
     * @param {number} x
     * @param {number} y
     * @param {PHShape} shape
     * @param {PHDimensions} dimensions
     * @param {number} color
     * @returns {Phaser.GameObjects.Image}
     * @memberof PlaceholderPlugin
     */
    public image(scene: BaseScene, x: number,y: number,shape: PHShape, dimensions: PHDimensions, color: number): Phaser.GameObjects.Image {
        const key = this.generateTexture(scene,shape, dimensions, 0xFFFFFF);
        const image = scene.add.image(x,y,key);
        image.tint = color;
        return image;
    }

    /**
     * generates a texture (if not aready existing in texture cahe) and returns it's key.
     *
     * @private
     * @param {BaseScene} scene
     * @param {PHShape} shape
     * @param {PHDimensions} dimensions
     * @returns {string}
     * @memberof PlaceholderPlugin
     */
    private generateTexture(scene: BaseScene, shape: PHShape, dimensions: PHDimensions, color: number = 0xffffff): string {

        // apply some defaults, TODO: needs better error checking based on the Shape type.
        if (dimensions.width === undefined) {dimensions.width = dimensions.radius * 2;}
        if (dimensions.height === undefined) { dimensions.height = dimensions.width; }
        if (dimensions.radius === undefined) { dimensions.radius = dimensions.width; }

        // get a key of the texture
        const key = this.generateTextureKey(shape, dimensions);

        // if this texture hasn't been generated before
        if (!this.game.textures.exists(key)) {

            // grab a canvas but dont add to display list.
            const _canvas = scene.make.graphics({ x: 0, y: 0 }, false);

            // we always fill with white and tint the sprites / images after.
            _canvas.fillStyle(color, 1);

            // draw desired shap / dimensions.
            switch (shape) {
                case PHShape.ROUND_RECT:
                    _canvas.fillRoundedRect(0, 0, dimensions.width, dimensions.height, dimensions.radius);
                    break;
                case PHShape.RECT:
                    _canvas.fillRect(0, 0, dimensions.width, dimensions.height);

                    break;
                case PHShape.CIRCLE:
                    _canvas.fillCircle(dimensions.radius, dimensions.radius, dimensions.radius);
                    break;
                default:
                    console.warn("Unknown shape when generationg placeholder.");
                    break;

            }

            // save texture to the texture controller.
            _canvas.generateTexture(key,dimensions.width,dimensions.height);
        }

        return key;
    }

    /**
     * generate a texture key based on any unique values of a texture genreated by this class.
     *
     * @private
     * @param {PHShape} shape
     * @param {PHDimensions} dimensions
     * @returns {string}
     * @memberof PlaceholderPlugin
     */
    private generateTextureKey(shape: PHShape, dimensions: PHDimensions): string {
        return "sponge-placeholder-" +
            shape.toString() + "-" +
            dimensions.height.toString() + "-" +
            dimensions.width.toString() + "-" +
            ((dimensions.radius) ? dimensions.radius.toString() : "");
    }



}


